import type { ReactNode } from 'react'
import { EndpointHint, HelpIcon, LoadFailure, Switch, Skeleton } from '../../core/index.js'
import type { FacilityDeliveryRules } from '../../facilities/index.js'
import { useFacilityDelivery } from '../hooks.js'
import type { DeliveryGroupView, DeliveryTypeView } from '../types.js'
import { NotificationIcon } from './notificationIcons.js'
import { messageTitle } from './messageCards.js'

interface FacilityDeliveryCardProps {
  facilityId: string
  /** The draft's block, every field stated. */
  rules: FacilityDeliveryRules
  onChange: (rules: FacilityDeliveryRules) => void
  /** False for a facility administrator, who reads the settings. */
  canEdit?: boolean
}

const TIME_INPUT_CLASS = 'rounded border border-marine/30 bg-white px-2 py-1 text-sm font-bold text-ink focus:outline-none focus:border-marine focus:ring-2 focus:ring-marine/20 disabled:opacity-60'

function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-marine/10 last:border-b-0 gap-4">
      <div className="min-w-0">
        <div className="text-xs text-ink-mid">{label}</div>
        {hint && <p className="mt-0.5 max-w-md text-[10.5px] leading-snug text-ink-mid/80">{hint}</p>}
      </div>
      {children}
    </div>
  )
}

/** The type's card title, as the Messages tab names it, else the key in words. */
function typeLabel(type: DeliveryTypeView['type']): string {
  return messageTitle(type)
}

/**
 * The Notifications tab of a facility's Configuration page (SMS plan
 * section 5): the facility switch for texts, quiet hours in the facility's
 * zone, and one switch per notification type laid out by the groups a
 * worker chooses by. The layout - the groups, every type, the deployment's
 * default for each - comes from `GET /facilities/{facilityId}/delivery`;
 * the draft's `delivery` block is what the switches change, and the page's
 * Save writes it with the rest of the configuration.
 */
export function FacilityDeliveryCard({ facilityId, rules, onChange, canEdit = true }: FacilityDeliveryCardProps) {
  const layout = useFacilityDelivery(facilityId)
  const sms = rules.sms
  const update = (patch: Partial<FacilityDeliveryRules['sms']>): void => onChange({ sms: { ...sms, ...patch } })
  const setType = (type: string, texted: boolean, fallback: boolean): void => {
    // Back at the deployment's default, the facility's own entry goes: a later change of the policy then follows.
    const { [type]: _own, ...rest } = sms.types
    update({ types: texted === fallback ? rest : { ...rest, [type]: texted } })
  }

  return (
    <div className="bg-white border border-marine/15 rounded p-5">
      <div className="text-sm font-black text-ink mb-2">
        Notifications
        <HelpIcon topic="messages.facility-delivery" className="ml-1" />
        <EndpointHint className="ml-2">GET /facilities/{'{facilityId}'}/delivery</EndpointHint>
      </div>
      <p className="mb-2 text-[11px] text-ink-mid leading-relaxed">
        Every message is in the worker's app first; a text is a nudge towards it. This says which kinds of message this facility texts and when. A worker chooses what they want within that on their own Notifications card, and neither side widens the other. Push and email are not shaped here.
      </p>
      {layout.data && !layout.data.sms.offered && <p className="mb-2 rounded bg-gold/10 px-3 py-2 text-[11px] text-ink-mid">This deployment has no text channel yet. The settings are kept and apply once texting is switched on.</p>}
      <Row label="Text workers from this facility" hint="Off, nothing is texted from this facility, whatever a worker chose.">
        <Switch checked={sms.enabled} onChange={(enabled) => update({ enabled })} label="Text workers from this facility" disabled={!canEdit} />
      </Row>
      <Row label="Quiet hours" hint={`In the facility's zone${layout.data ? ` (${layout.data.sms.timezone})` : ''}. A text that falls in them is held to the end, except an open shift or swap, which cannot wait for morning. May cross midnight.`}>
        <span className="flex items-center gap-2">
          <Switch checked={sms.quietHours !== null} onChange={(on) => update({ quietHours: on ? { start: '21:00', end: '07:00' } : null })} label="Quiet hours" disabled={!canEdit} />
          {sms.quietHours && (
            <>
              <input type="time" aria-label="Quiet hours start" className={TIME_INPUT_CLASS} value={sms.quietHours.start} disabled={!canEdit} onChange={(event) => update({ quietHours: { start: event.target.value, end: sms.quietHours?.end ?? '07:00' } })} />
              <span className="text-xs text-ink-mid">to</span>
              <input type="time" aria-label="Quiet hours end" className={TIME_INPUT_CLASS} value={sms.quietHours.end} disabled={!canEdit} onChange={(event) => update({ quietHours: { start: sms.quietHours?.start ?? '21:00', end: event.target.value } })} />
            </>
          )}
        </span>
      </Row>

      <div className="mt-4 text-xs font-bold text-ink">Which messages are texted</div>
      <p className="mb-1 text-[10.5px] text-ink-mid/80">"Default" marks the deployment's own policy for a kind of message. Urgent kinds go through quiet hours.</p>
      {layout.isPending && <Skeleton />}
      {layout.isError && <LoadFailure size="xs" message="The list of message kinds could not be loaded." onRetry={() => void layout.refetch()} retrying={layout.isFetching} />}
      {layout.data &&
        layout.data.sms.groups.map((group) => (
          <GroupTable key={group.key} group={group} types={sms.types} disabled={!canEdit || !sms.enabled} onChange={setType} />
        ))}
    </div>
  )
}

function GroupTable({ group, types, disabled, onChange }: { group: DeliveryGroupView; types: Record<string, boolean>; disabled: boolean; onChange: (type: string, texted: boolean, fallback: boolean) => void }) {
  return (
    <div className="mt-3">
      <div className="text-[11px] font-bold text-ink">{group.label}</div>
      <p className="text-[10.5px] text-ink-mid/80">{group.description}</p>
      <div className="mt-1 divide-y divide-marine/10 rounded border border-marine/15">
        {group.types.map((entry) => {
          const texted = types[entry.type] ?? entry.default
          const isDefault = types[entry.type] === undefined
          return (
            <div key={entry.type} className="flex items-center justify-between gap-3 px-3 py-1.5">
              <span className="flex min-w-0 items-center gap-2 text-[11px] text-ink">
                <NotificationIcon type={entry.type} size="sm" />
                <span className="truncate">{typeLabel(entry.type)}</span>
                {entry.urgent && <span className="rounded bg-coral/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-coral">Urgent</span>}
                {isDefault && <span className="text-[10px] text-ink-mid">· Default</span>}
              </span>
              <Switch checked={texted} onChange={(next) => onChange(entry.type, next, entry.default)} label={`Text ${typeLabel(entry.type)}`} disabled={disabled} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
